import { useState } from "react";
import {
  Loader2,
  Inbox,
  AlertCircle,
  RotateCcw,
  Plus,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Clock,
  Sparkles,
} from "lucide-react";
import { Badge, Button, StatusBadge } from "@/shared/ui";
import { cn } from "@/shared/lib/cn";
import { TestUiUnifiedStatusMatrix } from "./TestUiUnifiedStatusMatrix";

export function TestUiStatesSection() {
  const [isSimulatingLoading, setIsSimulatingLoading] = useState(false);

  const statuses = [
    { key: "Draft", label: "Nháp (Draft)" },
    { key: "Confirmed", label: "Đã duyệt / Xác nhận (Confirmed)" },
    { key: "In-progress", label: "Đang sản xuất (In-progress)" },
    { key: "Completed", label: "Hoàn tất xuất xưởng (Completed)" },
    { key: "Hold", label: "Tạm giữ kiểm tra CCP (Hold)" },
    { key: "Quarantine", label: "Cách ly dịch bệnh (Quarantine)" },
    { key: "Cancelled", label: "Đã hủy bỏ (Cancelled)" },
    { key: "Passed", label: "KCS Đạt chuẩn (Passed)" },
    { key: "Failed", label: "KCS Không đạt (Failed)" },
  ];

  return (
    <section className="space-y-6">
      {/* 0. Bảng Thống Nhất Màu Sắc Toàn Hệ Thống (Master Color Matrix) */}
      <TestUiUnifiedStatusMatrix />
      <div>
        <h2 className="m-0 text-h2 font-bold text-text">
          Chuẩn Hóa Các Trạng Thái Giao Diện (States & Status Badges)
        </h2>
        <p className="m-0 text-caption text-text-muted">
          Đảm bảo đầy đủ 4 trạng thái cốt lõi: Loading, Empty, Error, và Bảng mã trạng thái đồng bộ
        </p>
      </div>

      {/* 1. Status Badges Palette */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
        <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2 border-b border-border/60 pb-2">
          <Sparkles className="size-4 text-primary" />
          <span>1. Ma Trận Màu Trạng Thái Nghiệp Vụ (StatusBadge Matrix)</span>
        </h3>
        <p className="m-0 text-caption text-text-muted">
          Sử dụng duy nhất component `StatusBadge` từ `@/shared/ui` — Tuyệt đối không tự gõ class màu riêng biệt
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
          {statuses.map((s) => (
            <div
              key={s.key}
              className="flex items-center justify-between rounded-control border border-border bg-bg/50 px-3 py-2"
            >
              <span className="text-caption text-text-secondary">{s.label}</span>
              <StatusBadge status={s.key} />
            </div>
          ))}
        </div>
      </div>

      {/* 2. Loading State (Skeleton Shimmer) */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
        <div className="flex items-center justify-between border-b border-border/60 pb-2">
          <div>
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <Loader2 className="size-4 text-primary animate-spin" />
              <span>2. Chuẩn Loading Skeleton (Khi Đang Tải Dữ Liệu)</span>
            </h3>
            <p className="m-0 text-caption text-text-muted">
              Sử dụng hiệu ứng shimmer dạng khối thay vì chỉ hiện spinner đơn điệu
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setIsSimulatingLoading(true);
              setTimeout(() => setIsSimulatingLoading(false), 2000);
            }}
          >
            Mô phỏng 2s
          </Button>
        </div>

        <div className="space-y-3 pt-2">
          {/* Skeleton KPI row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse rounded-control border border-border/60 bg-bg p-3 space-y-2">
                <div className="h-3 w-20 rounded bg-border" />
                <div className="h-6 w-16 rounded bg-border" />
              </div>
            ))}
          </div>

          {/* Skeleton Table rows */}
          <div className="animate-pulse space-y-2 rounded-control border border-border/60 bg-bg p-3">
            <div className="h-4 w-full rounded bg-border/80" />
            <div className="h-4 w-5/6 rounded bg-border/60" />
            <div className="h-4 w-4/6 rounded bg-border/50" />
            <div className="h-4 w-full rounded bg-border/60" />
          </div>
        </div>
      </div>

      {/* 3. Empty State */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
        <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2 border-b border-border/60 pb-2">
          <Inbox className="size-4 text-primary" />
          <span>3. Chuẩn Màn Hình Trống (Empty State Standard)</span>
        </h3>

        <div className="flex flex-col items-center justify-center rounded-control border-2 border-dashed border-border bg-bg/40 p-8 text-center">
          <div className="flex size-14 items-center justify-center rounded-full bg-primary-soft text-primary shadow-xs">
            <Inbox className="size-7" />
          </div>
          <h4 className="m-0 mt-3 text-body font-bold text-text">
            Chưa có Lệnh sản xuất nào trong ca này
          </h4>
          <p className="m-0 mt-1 max-w-md text-caption text-text-muted">
            Hiện tại xưởng chưa nhận kế hoạch phân bổ từ phòng Điều độ. Bạn có thể tạo lệnh sản xuất mới hoặc đồng bộ kế hoạch từ phòng Kế hoạch (PLN).
          </p>
          <div className="mt-4 flex items-center gap-2">
            <Button size="sm" className="flex items-center gap-1.5">
              <Plus className="size-4" />
              <span>Tạo lệnh mới ngay</span>
            </Button>
            <Button variant="secondary" size="sm">
              Tải lại trang
            </Button>
          </div>
        </div>
      </div>

      {/* 4. Error State */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
        <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2 border-b border-border/60 pb-2">
          <AlertCircle className="size-4 text-danger-fg" />
          <span>4. Chuẩn Thông Báo Lỗi Kết Nối (Error State Standard)</span>
        </h3>

        <div className="flex items-start gap-3 rounded-control border border-danger/40 bg-danger-soft/40 p-4 text-body">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-danger text-on-primary">
            <AlertCircle className="size-5" />
          </div>
          <div className="flex-1 space-y-1">
            <div className="font-bold text-danger-fg">
              Không thể tải thông tin giám sát nhiệt độ kho âm (Lỗi kết nối Sensor IoT)
            </div>
            <p className="m-0 text-caption text-text-secondary">
              Mã lỗi: <code>ERR_IOT_TIMEOUT_504</code>. Cổng giao tiếp Modbus RTU tại Kho Cần Thơ không phản hồi trong 15 giây.
            </p>
            <div className="pt-2">
              <Button size="sm" variant="danger" className="h-7 text-caption">
                <RotateCcw className="mr-1 size-3.5" /> Thử kết nối lại
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
